/**
 * UserEngagementBanner — persistent nudge that combines profile-
 * completion + FreedrIQ status so a user only sees ONE prompt on the
 * hub pages instead of two competing banners.
 *
 * Reads GET /api/user/engagement-status. Renders only when
 * `should_prompt` is true. Session-dismissible so the nag returns
 * next login. Admins are exempted server-side.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { ClipboardList, Sparkles, X, ArrowRight } from 'lucide-react';

const SESSION_KEY = 'fdacademy_engagement_banner_dismissed';

export default function UserEngagementBanner() {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    if (!user) return undefined;
    let cancel = false;
    axios.get(`${API}/user/engagement-status`).then(({ data }) => {
      if (!cancel) setState(data);
    }).catch(() => {});
    return () => { cancel = true; };
  }, [API, user]);

  if (!user || dismissed || !state || !state.should_prompt) return null;

  const profileIncomplete  = !state.profile?.complete;
  const freedriqIncomplete = !state.freedriq?.started;
  const pct = Math.max(0, Math.min(100, Math.round(state.profile?.percentage || 0)));

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (_) {}
  };

  const goProfile = () => { window.location.href = '/portal?tab=profile'; };
  const goFreedrIQ = () => navigate('/freedriq?tier=free&phase=pre');

  return (
    <section className="container mx-auto px-6 pt-6" data-testid="user-engagement-banner">
      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 sm:p-5 flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-xl bg-amber-500 text-navy flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800 dark:text-amber-300">
            Finish setting up your account
          </div>
          <h2 className="font-heading text-lg sm:text-xl font-extrabold text-foreground mt-1">
            {profileIncomplete && freedriqIncomplete
              ? 'Two quick things left'
              : profileIncomplete
                ? `Finish your profile — ${pct}% complete`
                : 'Take the FreedrIQ entry assessment'}
          </h2>
          <p className="mt-1 text-xs text-foreground/70">
            {profileIncomplete && freedriqIncomplete
              ? 'Finish your profile and take the 10-minute FreedrIQ assessment so we can tailor tools, recommendations, and events to you.'
              : profileIncomplete
                ? 'Machine + demographic data powers cohort insights, better AI recommendations, and event invites near you.'
                : 'A 10-minute assessment measures your baseline so lessons meet you where you actually are.'}
          </p>
          {profileIncomplete && (
            <div className="mt-3 h-1.5 rounded-full bg-amber-500/20 overflow-hidden">
              <div
                className="h-full bg-amber-500 transition-all"
                style={{ width: `${pct}%` }}
                data-testid="engagement-banner-progress"
              />
            </div>
          )}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {profileIncomplete && (
              <Button
                size="sm"
                onClick={goProfile}
                className="rounded-full font-bold bg-amber-500 text-navy hover:bg-amber-500/90"
                data-testid="engagement-banner-profile-cta"
              >
                <ClipboardList className="w-3.5 h-3.5 mr-1" />
                Finish profile <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
            {freedriqIncomplete && (
              <Button
                size="sm"
                onClick={goFreedrIQ}
                variant={profileIncomplete ? 'outline' : 'default'}
                className={profileIncomplete
                  ? 'rounded-full font-bold border-amber-500 text-amber-800 dark:text-amber-200 hover:bg-amber-500/10'
                  : 'rounded-full font-bold bg-amber-500 text-navy hover:bg-amber-500/90'}
                data-testid="engagement-banner-freedriq-cta"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1" />
                Take FreedrIQ <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="h-7 w-7 rounded-full hover:bg-amber-500/20 flex items-center justify-center"
          aria-label="Dismiss for this session"
          data-testid="engagement-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}
